import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Settings, Link2, MessageSquare, Calendar, ArrowRight, X, Lock, Loader2, Monitor, Bell, MousePointerClick, ToggleRight, RefreshCw, PenLine, ShieldCheck, Zap, Sparkles, CheckCircle2 } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
import { setAuthSessionCookies } from "@/lib/authCookies";
import {
  mapTrialSignupError,
  registerTrialAccount,
  TRIAL_BOT_TYPES,
} from "@/lib/landingSignup";
import { trackRegistrationIfAuthenticated } from "@/lib/leadEvents";
import { appLoginPath } from "@/lib/landingPaths";
import { formatMinorAmount } from "@/constants/billingCatalog";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";
import SiteLogo from "@/components/SiteLogo";
import { usePublicBillingCatalog } from "@/hooks/usePublicBillingCatalog";
import { realEstateSubscribeLabel } from "@/lib/landingCatalogCopy";
import { getSiteBrand } from "@/lib/siteHosts";

// VSL thumbnail placeholder
const VSL_THUMBNAIL = "/images/thumbnail.png";

// Email validation helper function
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Email Popup Modal Component
const EmailPopupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  if (!isOpen) return null;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear error when user starts typing after an error
    if (touched && value) {
      if (validateEmail(value)) {
        setError("");
      } else {
        setError("Please enter a valid email address");
      }
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (!email) {
      setError("Email is required");
    } else if (!validateEmail(email)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    
    // Validate before submission
    if (!email) {
      setError("Email is required");
      return;
    }
    
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Step 1: Register user
      const registerResponse = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email: email
      });

      const registerData = registerResponse.data;

      // Check if user already exists
      if (registerData.status === "error") {
        setError(registerData.message || "An error occurred. Please try again.");
        setIsLoading(false);
        return;
      }

      // Step 2: User registered successfully - Save token and email in cookies
      const token = registerData.token;
      const botType = registerData.bot_type || "real_estate";
      
      // Set cookies with 7 days expiry
      Cookies.set("token", token, { expires: 7, secure: true, sameSite: "strict" });
      Cookies.set("email", email.toLowerCase(), { expires: 7, secure: true, sameSite: "strict" });
      Cookies.set("bot_type", botType, { expires: 7, secure: true, sameSite: "strict" });

      // Step 3: Create Stripe subscription session
      const stripeResponse = await axios.post(
        `${BACKEND_URL}/api/stripe/create-subscription`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const stripeData = stripeResponse.data;

      if (stripeData.status === "ok" && stripeData.session_url) {
        // Step 4: Redirect to Stripe checkout
        window.location.href = stripeData.session_url;
      } else {
        setError("Failed to create payment session. Please try again.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Registration/Subscription error:", err);
      
      // Handle different error scenarios
      if (err.response) {
        // Server responded with an error status
        const errorMessage = err.response.data?.detail || err.response.data?.message || "An error occurred. Please try again.";
        setError(errorMessage);
      } else if (err.request) {
        // Network error - no response received
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const isValid = email && validateEmail(email);

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={handleBackdropClick}
      data-testid="email-popup-overlay"
    >
      <div 
        className="relative w-full max-w-md glass rounded-2xl p-8 border border-[#00D1FF]/20 animate-fade-in-up"
        data-testid="email-popup-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors disabled:opacity-50"
          data-testid="popup-close-button"
          aria-label="Close popup"
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-6 pr-6">
          Where should we send your Digital Real Estate Agent access?
        </h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Input */}
          <div>
            <label htmlFor="email-input" className="block text-[#9CA3AF] text-sm mb-2 font-body">
              Email
            </label>
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleBlur}
              placeholder="your@email.com"
              disabled={isLoading}
              className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                error && touched
                  ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                  : isValid
                  ? "border-green-500/50 focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                  : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
              }`}
              data-testid="email-input"
            />
            {/* Error Message */}
            {error && touched && (
              <p className="mt-2 text-red-400 text-xs font-body" data-testid="email-error">
                {error}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
              isValid && !isLoading
                ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
            }`}
            data-testid="popup-submit-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Activate My Digital Real Estate Agent
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          {/* Security Note */}
          <p className="text-[#9CA3AF]/70 text-xs text-center flex items-center justify-center gap-1.5 pt-2">
            <Lock className="w-3 h-3" />
            Secure payment via Stripe on the next step
          </p>
        </form>
      </div>
    </div>
  );
};

// Free Demo Popup Modal Component
const FreeDemoPopupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const DEMO_AGENCY_NAME = "Premium Property Group";

  if (!isOpen) return null;

  const resetState = () => {
    setEmail("");
    setError("");
    setTouched(false);
    setIsLoading(false);
    setIsSuccess(false);
    setRegisteredEmail("");
  };

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (touched && value) {
      if (validateEmail(value)) {
        setError("");
      } else {
        setError("Please enter a valid email address");
      }
    } else if (touched && !value) {
      setError("Email is required");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (!email) {
      setError("Email is required");
    } else if (!validateEmail(email)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (!email) {
      setError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = await registerTrialAccount({
        email,
        botType: TRIAL_BOT_TYPES.REAL_ESTATE,
      });

      if (data.status === "ok") {
        const token = data.token;
        const botType = data.bot_type || TRIAL_BOT_TYPES.REAL_ESTATE;
        const normalizedEmail = email.toLowerCase();

        setAuthSessionCookies({
          token,
          email: normalizedEmail,
          accountType: data.account_type || "client",
          botType,
        });

        trackRegistrationIfAuthenticated().catch((err) =>
          console.warn("[leadEvents] is_registered failed", err),
        );

        setRegisteredEmail(normalizedEmail);
        setIsSuccess(true);
        setIsLoading(false);
      } else if (data.status === "error") {
        if (data.message === "The user already exists") {
          setError("This email is already in use. Please use a different email to create a demo account.");
        } else {
          setError(data.message || "An error occurred. Please try again.");
        }
        setIsLoading(false);
      } else {
        setError("Unexpected response from the server. Please try again.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Demo registration error:", err);
      setError(
        mapTrialSignupError(
          err,
          "This email is already in use. Please use a different email to create a demo account."
        )
      );
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const isValid = email && validateEmail(email);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6 overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="free-demo-popup-overlay"
    >
      <div
        className="relative w-full max-w-lg glass rounded-2xl p-8 border border-[#00D1FF]/20 animate-fade-in-up my-auto"
        data-testid="free-demo-popup-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors disabled:opacity-50"
          data-testid="free-demo-close-button"
          aria-label="Close popup"
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </button>

        {!isSuccess ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#00D1FF]" />
              </div>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-2 pr-6">
              Start Your Free Demonstration
            </h2>
            <p className="font-body text-sm text-[#9CA3AF] text-center mb-6 leading-relaxed">
              Enter your email and we'll provision a fully-seeded sandbox so you can experience your Digital Real Estate Agent in seconds.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="free-demo-email-input" className="block text-[#9CA3AF] text-sm mb-2 font-body">
                  Email
                </label>
                <input
                  id="free-demo-email-input"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                    error && touched
                      ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                      : isValid
                      ? "border-green-500/50 focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                      : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                  }`}
                  data-testid="free-demo-email-input"
                />
                {error && (
                  <p className="mt-2 text-red-400 text-xs font-body" data-testid="free-demo-email-error">
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValid || isLoading}
                className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                  isValid && !isLoading
                    ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                    : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                }`}
                data-testid="free-demo-next-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Provisioning sandbox...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-[#9CA3AF]/70 text-xs text-center flex items-center justify-center gap-1.5 pt-2">
                <Lock className="w-3 h-3" />
                100% free. No credit card required.
              </p>
            </form>
          </>
        ) : (
          <div data-testid="free-demo-success-state">
            {/* Success Header */}
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-4 pr-6">
              Sandbox Ready
            </h2>

            {/* Success Body */}
            <div className="space-y-4 font-body text-sm md:text-[15px] text-[#D1D5DB] leading-relaxed">
              <p>
                We have successfully provisioned your sandbox environment. The infrastructure is live and pre-configured for{" "}
                <span className="font-semibold text-[#F3F4F6]">{DEMO_AGENCY_NAME}</span>.
              </p>
              <p>
                To ensure a realistic test, I have seeded your database with two sample properties (one for sale, one for rent) so you can immediately see how I handle specific listing inquiries.
              </p>

              {/* Credentials Card */}
              <div className="glass rounded-xl p-4 border border-[#00D1FF]/20 space-y-2">
                <p className="font-heading font-bold text-[#F3F4F6] text-sm">Your Access Credentials:</p>
                <p className="break-all">
                  <span className="mr-1">🔗</span>
                  <span className="text-[#9CA3AF]">Portal: </span>
                  <Link
                    to={appLoginPath()}
                    className="text-[#00D1FF] hover:underline"
                    data-testid="free-demo-portal-link"
                  >
                    {typeof window !== "undefined" ? `${window.location.origin}${appLoginPath()}` : appLoginPath()}
                  </Link>
                </p>
                <p className="break-all">
                  <span className="mr-1">👤</span>
                  <span className="text-[#9CA3AF]">Email: </span>
                  <span className="text-[#F3F4F6] font-mono text-xs md:text-sm" data-testid="free-demo-shown-email">
                    {registeredEmail}
                  </span>
                </p>
                <p>
                  <span className="mr-1">🔑</span>
                  <span className="text-[#9CA3AF]">Password has been sent to your email.</span>
                </p>
              </div>

              {/* Next Steps */}
              <div>
                <p className="font-heading font-bold text-[#F3F4F6] text-sm mb-2">Next Steps:</p>
                <ol className="space-y-2 list-decimal pl-5 marker:text-[#00D1FF]">
                  <li>
                    <span className="font-semibold text-[#F3F4F6]">'Open AI Simulation'</span> tab to test my{" "}
                    <span className="text-[#00D1FF] font-mono">9.26s</span> response speed.
                  </li>
                  <li>
                    Visit <span className="font-semibold text-[#F3F4F6]">'Digital Brain Settings'</span> or{" "}
                    <span className="font-semibold text-[#F3F4F6]">'Listings'</span> whenever you are ready to update the agency profile or upload your real inventory.
                  </li>
                </ol>
              </div>
            </div>

            {/* Open AI Simulation Button */}
            <Link
              to="/dashboard/chat-test"
              className="mt-6 w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
              data-testid="free-demo-open-simulation-button"
              onClick={() => {
                resetState();
                onClose();
              }}
            >
              Open AI Simulation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// WhatsApp Icon (inline SVG for brand accuracy)
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.463 3.488" />
  </svg>
);

// Telegram Icon (inline SVG for brand accuracy)
const TelegramIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

// Quick Questions Popup Modal (WhatsApp / Telegram channel chooser)
const QuickQuestionsPopupModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const WHATSAPP_URL = "https://wa.me/50671230590?text=Hi!";
  const TELEGRAM_URL = "https://t.me/FractoLabsAiBrokerBot";

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={handleBackdropClick}
      data-testid="quick-questions-popup-overlay"
    >
      <div
        className="relative w-full max-w-md glass rounded-2xl p-8 border border-[#00D1FF]/20 animate-fade-in-up"
        data-testid="quick-questions-popup-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
          data-testid="quick-questions-close-button"
          aria-label="Close popup"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Title */}
        <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-3 pr-6">
          Get Instant Answers
        </h2>

        {/* Subtitle */}
        <p className="font-body text-sm md:text-base text-[#9CA3AF] text-center leading-relaxed mb-6">
          Inquire about pricing, technical CRM integrations, or our lead security protocols. Choose your preferred channel and the {getSiteBrand().name} Digital Agent Specialist will provide a response in seconds.
        </p>

        {/* Channel Buttons */}
        <div className="space-y-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="w-full flex items-center justify-center gap-3 font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 bg-[#25D366] text-white hover:bg-[#20bd5a]"
            data-testid="quick-questions-whatsapp-button"
          >
            <WhatsAppIcon className="w-6 h-6" />
            Ask via WhatsApp
          </a>

          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="w-full flex items-center justify-center gap-3 font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 bg-[#229ED9] text-white hover:bg-[#1d86b8]"
            data-testid="quick-questions-telegram-button"
          >
            <TelegramIcon className="w-6 h-6" />
            Ask via Telegram
          </a>
        </div>
      </div>
    </div>
  );
};

// Quick Questions CTA Section (placed below the main VSL block)
const QuickQuestionsCTA = ({ onOpenQuickPopup }) => (
  <section
    className="relative px-6 pb-16 -mt-4"
    data-testid="quick-questions-cta-section"
  >
    <div className="max-w-4xl mx-auto text-center">
      <button
        onClick={onOpenQuickPopup}
        className="group inline-flex items-center gap-2 glass border border-[#00D1FF]/30 text-[#F3F4F6] font-heading font-semibold text-sm md:text-base py-3 px-6 rounded-lg hover:border-[#00D1FF]/60 hover:bg-[#00D1FF]/5 transition-all duration-300"
        data-testid="quick-questions-cta-button"
      >
        <Zap className="w-4 h-4 text-[#00D1FF]" />
        Have product questions? Get an answer in ~9.26s
        <span className="text-[#00D1FF]">*</span>
      </button>
      <p className="mt-3 text-[#9CA3AF]/70 text-xs md:text-xs font-body max-w-2xl mx-auto leading-relaxed">
        *9.26s is our benchmarked infrastructure latency. Actual delivery speed may vary slightly depending on Meta/Telegram network load.
      </p>
    </div>
  </section>
);

// Header Component
const Header = ({ onOpenPopup }) => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
      <SiteLogo className="h-6 md:h-7 w-auto" testId="header-logo" />
      <div className="flex items-center gap-4">
        <Link
          to={appLoginPath()}
          className="text-[#9CA3AF] font-body text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="header-login-link"
        >
          Login
        </Link>
        <button 
          onClick={onOpenPopup}
          className="bg-[#00D1FF] text-[#0F1115] font-heading font-bold text-sm py-2 px-5 rounded-lg hover:bg-[#00D1FF]/90 transition-colors duration-300"
          data-testid="header-cta-button"
        >
          Get Started Now
        </button>
      </div>
    </div>
  </header>
);

// Hero Section
const HeroSection = () => (
  <section className="relative flex items-center justify-center pt-40 pb-4 px-6 radial-gradient-hero" data-testid="hero-section">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#F3F4F6] tracking-tight leading-tight mb-6 animate-fade-in-up">
        Stop Losing $2M Leads: Hire Your Digital Real Estate Agent Right Now
      </h1>
      <p className="font-body text-base md:text-lg text-[#9CA3AF] max-w-2xl mx-auto animate-fade-in-up animate-delay-100">
        Watch a 2-minute demo of an Digital Real Estate Agent that responds instantly and nurtures leads 24/7.
      </p>
    </div>
  </section>
);

// VSL Video Section
const VSLSection = ({ onOpenPopup, onOpenFreeDemoPopup, entryOffer }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const YOUTUBE_VIDEO_ID = "-BagyWtZRO4";

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  return (
    <section className="relative pt-6 pb-16 px-6 radial-gradient-video" data-testid="vsl-section">
      <div className="max-w-4xl mx-auto">
        {/* Video Container */}
        <div 
          className="relative aspect-video rounded-3xl overflow-hidden glass video-glow border border-[#00D1FF]/30"
          data-testid="video-container"
        >
          {!isPlaying ? (
            <>
              {/* Thumbnail */}
              <img 
                src={VSL_THUMBNAIL} 
                alt="Video thumbnail" 
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300 cursor-pointer" onClick={handlePlayClick} />
              
              {/* Play Button */}
              <button 
                onClick={handlePlayClick}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 play-btn-pulse w-20 h-20 md:w-24 md:h-24 bg-[#00D1FF] rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                data-testid="play-button"
                aria-label="Play video"
              >
                <Play className="w-8 h-8 md:w-10 md:h-10 text-[#0F1115] ml-1" fill="#0F1115" />
              </button>
            </>
          ) : (
            /* YouTube Embedded Video */
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
              title="Digital Real Estate Agent Demo Video"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="youtube-player"
            />
          )}
        </div>

        {/* CTA Block */}
        <div className="mt-10 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            {/* Start Free Demonstration Button (left) */}
            <button
              onClick={onOpenFreeDemoPopup}
              className="inline-flex items-center justify-center gap-2 glass border border-[#00D1FF]/40 text-[#F3F4F6] font-heading font-bold text-base md:text-lg py-3.5 px-8 md:px-10 rounded-lg hover:border-[#00D1FF] hover:bg-[#00D1FF]/5 transition-all duration-300"
              data-testid="cta-button-free-demo"
            >
              <Sparkles className="w-5 h-5 text-[#00D1FF]" />
              Start Free Demonstration
            </button>

            {/* Connect Digital Real Estate Agent Button (right) */}
            <button
              onClick={onOpenPopup}
              className="btn-pulse bg-[#00D1FF] text-[#0F1115] font-heading font-bold text-lg md:text-xl py-4 px-8 md:px-12 rounded-lg hover:bg-[#00D1FF]/90 transition-colors duration-300"
              data-testid="cta-button-main"
            >
              <span data-testid="landing-catalog-price">
                {realEstateSubscribeLabel(entryOffer)}
              </span>
            </button>
          </div>

          <p className="mt-4 text-[#9CA3AF] text-sm md:text-base">
            Integration within 72 hours. No changes to your CRM.
          </p>
          
          {/* Payment Icons */}
          <div className="flex items-center justify-center mt-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-realtor-5/artifacts/wlxf5m7z_logo-stripe_back2.png" 
              alt="Stripe Payment" 
              className="h-12 w-auto grayscale opacity-70"
              data-testid="stripe-payment-logo"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Value Stack Section
const valueStackItems = [
  {
    icon: Settings,
    title: "Logic Setup",
    description: "Training the AI on your property database and your agency's tone of voice."
  },
  {
    icon: Link2,
    title: "Integration",
    description: WEBSITE_LEAD_INTERCEPTOR_COPY.realEstateIntegration,
  },
  {
    icon: MessageSquare,
    title: "Lead Qualification",
    description: "Setup of 4–5 key questions (budget, mortgage/cash, timeline) to filter out unqualified leads."
  },
  {
    icon: Calendar,
    title: "Calendar Sync",
    description: "The Digital Brain automatically books meetings into available time slots in the broker's calendar."
  }
];

function buildValueStackLead(entryOffer) {
  if (!entryOffer) {
    return "Start a 14-day trial — no setup fee — to power your Autonomous Revenue Engine. Includes AI Credits per month, instant smart alerts, and 24/7 uninterrupted lead engagement.";
  }
  const credits = Number(entryOffer.credits_included).toLocaleString();
  return (
    <>
      <span className="font-mono text-[#00D1FF]">
        {formatMinorAmount(entryOffer.amount_minor, entryOffer.currency)}
      </span>
      {" "}— no setup fee — to power your Autonomous Revenue Engine. Includes {credits} AI Credits per month, instant smart alerts, and 24/7 uninterrupted lead engagement.
    </>
  );
}

const ValueStackSection = ({ entryOffer }) => (
  <section className="py-20 px-6" data-testid="value-stack-section">
    <div className="max-w-7xl mx-auto">
      <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-center text-[#F3F4F6] mb-4">
        What's included in the turnkey system launch
      </h2>
      <p className="text-center text-[#9CA3AF] mb-12 text-sm md:text-base">
        {buildValueStackLead(entryOffer)}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {valueStackItems.map((item, index) => (
          <div 
            key={index}
            className="glass rounded-2xl p-6 card-hover"
            data-testid={`value-card-${index}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center mb-4">
              <item.icon className="w-6 h-6 text-[#00D1FF]" />
            </div>
            <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-2">
              {item.title}
            </h3>
            <p className="font-body text-sm text-[#9CA3AF] leading-relaxed">
              {item.description}
            </p>
          </div>
        ))}
      </div>

      {/* VIP Concierge Onboarding */}
      <div className="mt-10 glass rounded-2xl p-8 md:p-10 border border-[#00D1FF]/20" data-testid="vip-concierge-block">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-14 h-14 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center flex-shrink-0">
            <Monitor className="w-7 h-7 text-[#00D1FF]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] mb-2">
              VIP Concierge Onboarding
            </h3>
            <p className="font-body text-sm md:text-base text-[#9CA3AF] leading-relaxed">
              Included in onboarding. During a brief 15-minute screen-share Zoom session, our integration specialist will seamlessly connect your infrastructure to the AI. Sit back and enjoy your coffee while we handle 100% of the technical setup. You won't touch a single line of code.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Absolute Control Section
const controlFeatures = [
  {
    icon: Bell,
    title: "Instant Smart Alerts",
    description: "Receive immediate notifications the moment a new lead is captured, a meeting is booked, or human intervention is requested."
  },
  {
    icon: MousePointerClick,
    title: "Seamless Chat Takeover",
    description: "Every alert includes a direct link to the Dashboard. Click, review the AI's qualification summary, and seamlessly jump into the conversation."
  },
  {
    icon: ToggleRight,
    title: "1-Click Manual Override",
    description: "Ready to handle a high-stakes negotiation? You can pause the AI instantly for any specific chat and take over the communication manually at any time."
  }
];

const ControlSection = () => (
  <section className="py-20 px-6 bg-[#0a0c0f]" data-testid="control-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-center text-[#F3F4F6] mb-4">
        Absolute Control: The AI Works For You
      </h2>
      <p className="text-center text-[#9CA3AF] mb-12 text-sm md:text-base italic max-w-3xl mx-auto">
        You never lose the pulse of your business. Our infrastructure ensures you step in exactly when your expertise is needed to close the deal.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {controlFeatures.map((item, index) => (
          <div
            key={index}
            className="glass rounded-2xl p-6 card-hover"
            data-testid={`control-card-${index}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center mb-4">
              <item.icon className="w-6 h-6 text-[#00D1FF]" />
            </div>
            <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-2">
              {item.title}
            </h3>
            <p className="font-body text-sm text-[#9CA3AF] leading-relaxed">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Real-Time Inventory Section
const inventoryFeatures = [
  {
    icon: RefreshCw,
    title: "Automated API Synchronization",
    description: "Our team can integrate the AI directly with your property database or CRM. The Digital Brain automatically pulls real-time updates on pricing, availability, and property statuses without you lifting a finger."
  },
  {
    icon: PenLine,
    title: "Instant Manual Management",
    description: "Need to make a quick adjustment? Use the intuitive Dashboard to manually update listing details or adjust the AI's \"knowledge\" in seconds. You have the ultimate authority over what information is shared."
  },
  {
    icon: ShieldCheck,
    title: "Zero Discrepancy Guarantee",
    description: "The AI only discusses what's currently available. It's designed to stop conversations about sold properties, maintaining your agency's professional reputation and saving everyone's time."
  }
];

const InventorySection = () => (
  <section className="py-20 px-6" data-testid="inventory-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-center text-[#F3F4F6] mb-4">
        Real-Time Inventory: Your Listings, Always Current
      </h2>
      <p className="text-center text-[#9CA3AF] mb-12 text-sm md:text-base italic max-w-3xl mx-auto">
        Never worry about outdated prices or sold properties. Our system ensures the Digital Real Estate Agent operates with 100% accuracy, reflecting your actual inventory.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {inventoryFeatures.map((item, index) => (
          <div
            key={index}
            className="glass rounded-2xl p-6 card-hover"
            data-testid={`inventory-card-${index}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center mb-4">
              <item.icon className="w-6 h-6 text-[#00D1FF]" />
            </div>
            <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-2">
              {item.title}
            </h3>
            <p className="font-body text-sm text-[#9CA3AF] leading-relaxed">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// FAQ Section
function buildFaqItems(entryOffer) {
  const firstThree = [
    {
      question: "What if the Digital Brain starts making up prices or behaves rudely?",
      answer: "The AI is strictly constrained by system prompts. It only operates based on the uploaded knowledge base and guidelines. If a question is outside its scope, it politely transfers the conversation to a human manager."
    },
    {
      question: "Do I need to change my CRM or hire a developer?",
      answer: "No. The Digital Brain connects as an invisible layer on top of your existing messengers. We handle all technical setup end-to-end on our side."
    },
    {
      question: "Will my WhatsApp get blocked?",
      answer: "We use only official APIs or secure gateways fully compliant with Meta policies. The risk of blocking is zero."
    },
  ];
  if (!entryOffer) {
    return [
      ...firstThree,
      {
        question: "What exactly does the subscription cover? Are there hidden limits?",
        answer: "Your monthly fee covers the core infrastructure: 24/7 zero-downtime server hosting, instant smart alerts, automated API sync, and priority technical support. Additionally, every month includes a generous allowance of AI Credits. For 98% of agencies, this comfortably handles all incoming lead conversations for the month. If you run massive marketing campaigns and exceed this volume, you can upgrade to a higher tier for more monthly credits. Complete transparency, zero hidden fees.",
      },
    ];
  }
  const priceLine = formatMinorAmount(entryOffer.amount_minor, entryOffer.currency);
  const credits = entryOffer.credits_included;
  return [
    ...firstThree,
    {
      question: `What exactly does the ${priceLine} subscription cover? Are there hidden limits?`,
      answer: `Your ${priceLine} fee covers the core infrastructure: 24/7 zero-downtime server hosting, instant smart alerts, automated API sync, and priority technical support. Additionally, every month includes a generous allowance of ${credits} AI Credits. For 98% of agencies, this comfortably handles all incoming lead conversations for the month. If you run massive marketing campaigns and exceed this volume, you can upgrade to a higher tier for more monthly credits. Complete transparency, zero hidden fees.`,
    },
  ];
}

const FAQSection = ({ items }) => (
  <section className="py-20 px-6 bg-[#0a0c0f]" data-testid="faq-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-center text-[#F3F4F6] mb-12">
        Frequently Asked Questions
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {items.map((item, index) => (
          <div 
            key={index}
            className="border-b border-white/10 pb-6"
            data-testid={`faq-item-${index}`}
          >
            <h3 className="font-heading font-semibold text-base md:text-lg text-[#F3F4F6] mb-3">
              "{item.question}"
            </h3>
            <p className="font-body text-sm md:text-base text-[#9CA3AF] leading-relaxed">
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Onboarding Timeline Section
function buildTimelineSteps(entryOffer) {
  const payment = entryOffer
    ? `Subscribe to Tier 1 — ${formatMinorAmount(entryOffer.amount_minor, entryOffer.currency)} (no setup fee).`
    : "Subscribe to Tier 1 — start a trial (no setup fee).";
  return [
    {
      step: "01",
      title: "Payment",
      description: payment,
    },
    {
      step: "02",
      title: "Short Brief",
      description: "Next, you fill out a simple form about your agency."
    },
    {
      step: "03",
      title: `${getSiteBrand().name} Magic`,
      description: "We take your data, train the AI on your listings and your tone of voice."
    },
    {
      step: "04",
      title: "Launch",
      description: "Within 72 hours, we message you: \"The Digital Brain is ready.\" You test it yourself, and then we activate it for your clients."
    }
  ];
}

const OnboardingSection = ({ steps }) => (
  <section className="py-20 px-6" data-testid="onboarding-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-center text-[#F3F4F6] mb-4">
        4 steps to your first automated meeting
      </h2>
      <p className="text-center text-[#9CA3AF] mb-16 text-sm md:text-base">
        Get started in under 72 hours
      </p>
      
      {/* Desktop Timeline */}
      <div className="hidden md:grid grid-cols-4 gap-6 relative">
        {/* Connector Line */}
        <div className="absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#00D1FF]/50 via-[#00D1FF]/30 to-[#00D1FF]/10" />
        
        {steps.map((item, index) => (
          <div key={index} className="relative text-center" data-testid={`timeline-step-${index}`}>
            {/* Step Number */}
            <div className="w-12 h-12 rounded-full bg-[#00D1FF]/10 border border-[#00D1FF]/30 flex items-center justify-center mx-auto mb-4 relative z-10 bg-[#0F1115]">
              <span className="font-mono text-[#00D1FF] text-sm font-semibold">{item.step}</span>
            </div>
            <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-2">
              {item.title}
            </h3>
            <p className="font-body text-sm text-[#9CA3AF] leading-relaxed">
              {item.description}
            </p>
          </div>
        ))}
      </div>
      
      {/* Mobile Timeline */}
      <div className="md:hidden space-y-8">
        {steps.map((item, index) => (
          <div key={index} className="flex gap-4" data-testid={`timeline-step-mobile-${index}`}>
            {/* Step Number */}
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00D1FF]/10 border border-[#00D1FF]/30 flex items-center justify-center">
              <span className="font-mono text-[#00D1FF] text-sm font-semibold">{item.step}</span>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-1">
                {item.title}
              </h3>
              <p className="font-body text-sm text-[#9CA3AF] leading-relaxed">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Footer Section
const Footer = ({ onOpenPopup, onOpenQuickPopup, onOpenFreeDemoPopup }) => (
  <footer className="py-16 px-6 border-t border-white/5" data-testid="footer-section">
    <div className="max-w-4xl mx-auto text-center">
      {/* CTA Buttons */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
        {/* Start Free Demonstration Button (left) */}
        <button
          onClick={onOpenFreeDemoPopup}
          className="inline-flex items-center justify-center gap-2 glass border border-[#00D1FF]/40 text-[#F3F4F6] font-heading font-bold text-base md:text-lg py-3.5 px-8 md:px-10 rounded-lg hover:border-[#00D1FF] hover:bg-[#00D1FF]/5 transition-all duration-300"
          data-testid="cta-button-free-demo-footer"
        >
          <Sparkles className="w-5 h-5 text-[#00D1FF]" />
          Start Free Demonstration
        </button>

        {/* Get Started Now Button (right) */}
        <button
          onClick={onOpenPopup}
          className="btn-pulse bg-[#00D1FF] text-[#0F1115] font-heading font-bold text-lg md:text-xl py-4 px-8 md:px-12 rounded-lg hover:bg-[#00D1FF]/90 transition-colors duration-300 inline-flex items-center gap-2"
          data-testid="cta-button-footer"
        >
          Get Started Now
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      <p className="mt-4 text-[#9CA3AF] text-sm md:text-base">
        Integration within 72 hours. No changes to your CRM.
      </p>
      
      {/* Payment Icons */}
      <div className="flex items-center justify-center mt-6">
        <img 
          src="https://customer-assets.emergentagent.com/job_ai-realtor-5/artifacts/wlxf5m7z_logo-stripe_back2.png" 
          alt="Stripe Payment" 
          className="h-12 w-auto grayscale opacity-70"
          data-testid="stripe-payment-logo"
        />
      </div>

      {/* Quick Questions Secondary CTA */}
      <div className="mt-8 mb-8">
        <button
          onClick={onOpenQuickPopup}
          className="inline-flex items-center gap-2 glass border border-[#00D1FF]/30 text-[#F3F4F6] font-heading font-semibold text-sm md:text-base py-3 px-6 rounded-lg hover:border-[#00D1FF]/60 hover:bg-[#00D1FF]/5 transition-all duration-300"
          data-testid="quick-questions-cta-button-footer"
        >
          <Zap className="w-4 h-4 text-[#00D1FF]" />
          Have product questions? Get an answer in ~9.26s
          <span className="text-[#00D1FF]">*</span>
        </button>
        <p className="mt-3 text-[#9CA3AF]/70 text-xs font-body max-w-2xl mx-auto leading-relaxed">
          *9.26s is our benchmarked infrastructure latency. Actual delivery speed may vary slightly depending on Meta/Telegram network load.
        </p>
      </div>
      
      {/* Contact */}
      <p className="text-[#9CA3AF] text-sm mb-6">
        Questions? Contact us at{" "}
        <a href="mailto:support@fractolabs.com" className="text-[#00D1FF] hover:underline">
          support@fractolabs.com
        </a>
      </p>
      
      {/* Legal Links */}
      <div className="flex items-center justify-center gap-6 mb-4">
        <Link
          to="/privacy-policy"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="privacy-link"
        >
          Privacy Policy
        </Link>
        <span className="text-[#9CA3AF]/30">|</span>
        <Link
          to="/terms"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="terms-link"
        >
          Terms of Service
        </Link>
      </div>
      
      {/* Copyright */}
      <p className="text-[#9CA3AF]/60 text-xs font-mono">
        © 2026 {getSiteBrand().name}. All rights reserved.
      </p>
    </div>
  </footer>
);

// Landing Page Component
const LandingPage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isQuickPopupOpen, setIsQuickPopupOpen] = useState(false);
  const [isFreeDemoPopupOpen, setIsFreeDemoPopupOpen] = useState(false);
  const { entryOffer } = usePublicBillingCatalog();

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);
  const openQuickPopup = () => setIsQuickPopupOpen(true);
  const closeQuickPopup = () => setIsQuickPopupOpen(false);
  const openFreeDemoPopup = () => setIsFreeDemoPopupOpen(true);
  const closeFreeDemoPopup = () => setIsFreeDemoPopupOpen(false);

  return (
    <div className="min-h-screen bg-[#0F1115]" data-testid="landing-page">
      <Header onOpenPopup={openPopup} />
      <main>
        <HeroSection />
        <VSLSection onOpenPopup={openPopup} onOpenFreeDemoPopup={openFreeDemoPopup} entryOffer={entryOffer} />
        <QuickQuestionsCTA onOpenQuickPopup={openQuickPopup} />
        <ValueStackSection entryOffer={entryOffer} />
        <ControlSection />
        <InventorySection />
        <OnboardingSection steps={buildTimelineSteps(entryOffer)} />
        <FAQSection items={buildFaqItems(entryOffer)} />
      </main>
      <Footer onOpenPopup={openPopup} onOpenQuickPopup={openQuickPopup} onOpenFreeDemoPopup={openFreeDemoPopup} />
      <EmailPopupModal isOpen={isPopupOpen} onClose={closePopup} />
      <QuickQuestionsPopupModal isOpen={isQuickPopupOpen} onClose={closeQuickPopup} />
      <FreeDemoPopupModal isOpen={isFreeDemoPopupOpen} onClose={closeFreeDemoPopup} />
    </div>
  );
};

export default LandingPage;
